import React, { Component, Suspense, lazy, startTransition } from "react";

import { ApolloProvider } from "@apollo/client";
import { BrowserRouter } from "react-router-dom";

import PropTypes from "prop-types";
import { setUser as setSentryUser } from "@sentry/browser";
import { useFlag } from "@unleash/proxy-client-react";
import { client, persistent, ensureLocalCacheDefaults } from "./graphql/client";
import { logError } from "./helpers/logging";
import { forceLogOut, isLoggedIn } from "./helpers/auth";
import { isCapacitorNative } from "./helpers/isCapacitorNative";
import Routes from "./routes";
import StyledLoading from "./components/atoms/Loading/StyledLoading";
import NativeEntryRedirect from "./components/atoms/NativeEntryRedirect";
import NativeHardwareBack from "./components/atoms/NativeHardwareBack";
import { NativeTabHistoryProvider } from "./helpers/nativeTabHistory";
import AppErrorBoundary from "./components/molecules/AppErrorBoundary";
import { GET_AUTH } from "./graphql/resolvers/auth";
import { GET_LOCAL_SHOPPING_CART, RESET_LOCAL_SHOPPING_CART } from "./graphql/resolvers/cart";
import { FETCH_POLICY_CACHE_ONLY, UNLEASH_WEBSITE_UNDER_MAINTENANCE } from "./helpers/constants";
import { AU_COUNTRY_ID, getCountryIdFromUrl } from "./helpers/urls.helper";
import { getCountryIdOfLoggedInMemberInCache, setCountryIdOfLoggedInMemberInCache } from "./helpers/tools";
import { useWelcomePackCookie } from "./helpers/hooks";
import Maintenance from "./pages/Maintenance";

import { StyledToastContainer } from "./components/atoms/GlobalAlert";

/* Mounted once at the app shell — sibling of <Routes>, inside Router +
 * NativeTabHistoryProvider so router/tab-history context resolve. This
 * placement is load-bearing: putting the bar inside <Layout> means it
 * unmounts whenever Layout unmounts (every cross-Switch navigation in
 * routes.js, plus pages that wrap themselves in their own Layout),
 * producing a visible flash on iOS WKWebView. Per-route visibility is
 * handled inside the bar via shouldHideTabBar(pathname) and the
 * keyboard-open early return — it returns null on routes that should
 * hide it while keeping the component instance mounted. Lazy keeps the
 * chunk (BrowseSheet/MoreSheet/~13 PNG icons + Capacitor helpers) off
 * the web critical path. */
const NativeTabBar = lazy(() => import("./components/organisms/NativeTabBar"));

// TODO DEV-203 replace this with apollo-link-state
window.store = {};

// Signal to the prerender function that the page is not yet ready.
// This prevents it from capturing HTML before React has rendered content.
// Layout will flip this to true once its data has loaded; non-Layout
// routes fall through to network idle detection in the prerender.
// eslint-disable-next-line no-underscore-dangle
if (window.__PRERENDER__) {
  window.prerenderReady = false;
}

// Start cache restoration at module load so it runs in parallel with
// React initialization, rather than waiting for componentDidMount.
// Catch here to prevent unhandled rejection if it fails before
// componentDidMount attaches its own handler — errors are non-fatal
// (app loads with empty cache and auth checks handle missing data).
let cacheReadyPromise;
try {
  // eslint-disable-next-line no-underscore-dangle
  cacheReadyPromise = window.__PRERENDER__
    ? Promise.resolve()
    : (localStorage.getItem(import.meta.env.REACT_APP_STORE_LOCAL_STORAGE)
        ? // A restored blob can predate a local-state field; fill in any
          // missing @client defaults so unguarded readers can't crash on a
          // shape the persistence history never wrote.
          persistent.restore().then(() => ensureLocalCacheDefaults())
        : persistent.persist()
      ).catch((error) => logError(error));
} catch (error) {
  // localStorage can throw in privacy-restricted browsers/webviews
  logError(error);
  cacheReadyPromise = Promise.resolve();
}

/**
 * Renders our app entry point
 * */
class App extends Component {
  static propTypes = {
    history: PropTypes.shape({}),
  };

  static defaultProps = {
    history: {},
  };

  state = {
    loaded: false,
  };

  async componentDidMount() {
    // Await the cache restore/persist that was started at module load.
    // For prerender this resolves immediately (Promise.resolve()).
    try {
      await cacheReadyPromise;

      // eslint-disable-next-line no-underscore-dangle
      if (!window.__PRERENDER__) {
        // Auth and cart checks require the cache to be ready
        if (localStorage.getItem(import.meta.env.REACT_APP_STORE_LOCAL_STORAGE)) {
          const urlCountryId = getCountryIdFromUrl();

          // if auth cache is broken, logout and reset cache
          const authDataQuery = await client.query({
            query: GET_AUTH,
            options: { fetchPolicy: FETCH_POLICY_CACHE_ONLY },
          });
          const auth = authDataQuery?.data?.auth;
          const memberId = authDataQuery?.data?.auth?.memberId;
          if (!auth || (isLoggedIn() && !memberId)) {
            // CASE: When history or cache is manually cleared while user is logged in to GPD,
            // but environment keys: REACT_APP_STORE_LOCAL_STORAGE and REACT_APP_AUTH_LOCAL_STORAGE are TRUE,
            // However, authQuery.auth.memberId is null.
            forceLogOut();
          } else if (!isLoggedIn() && memberId) {
            // Stale auth cache: a memberId was persisted from a previous session but the
            // auth token is gone (e.g. logged out in another tab, or cleared cookies but not
            // localStorage). Reset the auth entry so queries that read memberId from the
            // Apollo cache don't send it to the backend with no token — which gets rejected
            // as UNAUTHORIZED and can bounce the user to /404/ or loop on token refresh.
            client.writeQuery({
              query: GET_AUTH,
              data: {
                auth: {
                  memberId: null,
                  token: null,
                  __typename: "AuthObjectStore",
                },
              },
            });
          } else if (isLoggedIn() && memberId) {
            // User is successfully logged in
            setSentryUser({ id: memberId });
            const cachedCountryId = getCountryIdOfLoggedInMemberInCache();
            if (!cachedCountryId) {
              // We want to populate the cache for the existing logged in users for AU website.
              // Cache will be populated upon a fresh new login, so in the future it will not be needed.
              setCountryIdOfLoggedInMemberInCache(AU_COUNTRY_ID);
            } else if (urlCountryId && cachedCountryId !== urlCountryId) {
              forceLogOut();
            }
          } else if (!isLoggedIn() && !memberId) {
            // User is not logged in
            const localShoppingCartQuery = await client.query({
              query: GET_LOCAL_SHOPPING_CART,
              options: { fetchPolicy: FETCH_POLICY_CACHE_ONLY },
            });
            const countryIdInLocalCart = localShoppingCartQuery?.data?.localShoppingCart?.countryId;
            if (countryIdInLocalCart !== urlCountryId) {
              await client.mutate({ mutation: RESET_LOCAL_SHOPPING_CART });
            }
          }
        }
      }
    } catch (error) {
      logError(error);
    }

    // Sets 'loaded' to true, so application can be loaded.
    // Wrapped in startTransition so the browser paints the spinner (current
    // state) before React commits the heavy Routes/Layout render. Without
    // this, both renders flush in one batch and the spinner never paints,
    // pushing FCP from ~330ms (spinner) to ~560ms (real content).
    startTransition(() => {
      this.setState({
        loaded: true,
      });
    });
  }

  render() {
    const { history } = this.props;
    const { loaded } = this.state;

    if (!loaded) return <StyledLoading />;

    persistent.getLogs(
      import.meta.env.REACT_APP_ENV_NAME === "local" ||
        import.meta.env.REACT_APP_ENV_NAME === "develop" ||
        import.meta.env.REACT_APP_ENV_NAME === "staging"
    );

    return (
      // Provides apollo client across the application
      <ApolloProvider client={client}>
        {/* Route-Based code splitting.  */}
        {/* https://reactjs.org/docs/code-splitting.html#route-based-code-splitting */}
        <BrowserRouter>
          <NativeTabHistoryProvider>
            <NativeEntryRedirect />
            <NativeHardwareBack />
            <AppErrorBoundary history={history}>
              <React.Suspense fallback={<StyledLoading />}>
                <Routes persistentCache={persistent} />
              </React.Suspense>
            </AppErrorBoundary>
            {isCapacitorNative() && (
              <Suspense fallback={null}>
                <NativeTabBar />
              </Suspense>
            )}
          </NativeTabHistoryProvider>
        </BrowserRouter>

        <StyledToastContainer />
      </ApolloProvider>
    );
  }
}

/**
 * Wraps the class component in a functional component so that we can use hooks.
 * */
const AppWrapper = (props) => {
  useWelcomePackCookie();
  const isWebsiteUnderMaintenance = useFlag(UNLEASH_WEBSITE_UNDER_MAINTENANCE);

  if (isWebsiteUnderMaintenance) {
    return <Maintenance />;
  }

  return <App {...props} />;
};

export default AppWrapper;
